export enum EcolabelStandard {
  EKOENERGY = "EKOENERGY",
  OK_POWER = "OK_POWER",
  OK_POWER_NEW = "OK_POWER_NEW",
  NATUREMADE_STAR = "NATUREMADE_STAR",
  GREEN_E = "GREEN_E",
  OTHER = "OTHER",
}

export enum EcolabelStatus {
  NOT_APPROVED = "NOT_APPROVED",
  PRE_APPROVED = "PRE_APPROVED",
  APPROVED = "APPROVED",
  PENDING_REVIEW = "PENDING_REVIEW",
  REJECTED = "REJECTED",
}

/** Canonical vocabularies (mirror app/ecolabels/models/ecolabel_models.py). */
export const RENEWABLE_TYPES = [
  "SOLAR",
  "WIND",
  "HYDRO",
  "GEOTHERMAL",
  "BIOENERGY",
  "MARINE",
  "OTHER",
] as const;
export type RenewableType = (typeof RENEWABLE_TYPES)[number];

export const REGISTRATION_AUTHORITIES = [
  "AIB",
  "IREC",
  "TIGR",
  "LGC",
  "REGO",
  "REC",
  "GEC",
  "TREC",
  "NFC",
  "NZECS",
  "OTHER",
] as const;

/** Display labels where the code differs from what EKOenergy uses in conversation. */
export const REGISTRATION_AUTHORITY_LABELS: Record<string, string> = {
  TREC: "T-REC",
};

export type RegistrationIdQuality =
  "VALID" | "PLAUSIBLE" | "MULTIPLE" | "SUSPECT" | "MISSING";

export interface EcolabelDevice {
  id: number;
  standard: EcolabelStandard;
  plant_ecolabel_id: string | null;
  name: string;
  country: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  renewable_type: string | null;
  technology_code: string | null;
  registration_authority: string | null;
  relevant_link: string | null;
  capacity: number | null;
  capacity_units: string;
  cod: string | null;
  status: EcolabelStatus;
  public_notes: string | null;
  registry_device_id: number | null;
  date_reviewed: string | null;
  valid_from: string | null;
  valid_through: string | null;
  created_at: string;
  updated_at: string;
  registration_id_quality: RegistrationIdQuality | null;
  submission_batch_id: number | null;
  submitted_by_company_id: number | null;
  rejection_reason: string | null;
  reviewed_at: string | null;
}

/** One device as the public I-REC device register (Evident) describes it, projected onto
 * our device fields by the backend proxy. Decimals travel as strings. */
export interface IrecDeviceLookup {
  code: string;
  name: string | null;
  country: string | null;
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  renewable_type: string | null;
  technology_code: string | null;
  capacity: string | null;
  cod: string | null;
  relevant_link: string;
  registration_authority: string;
}

export interface IrecDeviceLookupResponse {
  /** `unavailable`: the register could not be reached — distinct from `not_found`. */
  status: "found" | "not_found" | "unavailable";
  device: IrecDeviceLookup | null;
}

export interface EcolabelDeviceCreateRequest {
  standard: EcolabelStandard;
  plant_ecolabel_id?: string | null;
  name: string;
  country: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  renewable_type?: string | null;
  technology_code?: string | null;
  registration_authority?: string | null;
  relevant_link?: string | null;
  capacity?: number | null;
  capacity_units?: string;
  cod?: string | null;
  status?: EcolabelStatus;
  public_notes?: string | null;
  registry_device_id?: number | null;
  date_reviewed?: string | null;
  valid_from?: string | null;
  valid_through?: string | null;
}

export interface EcolabelDeviceUpdateRequest {
  standard?: EcolabelStandard;
  plant_ecolabel_id?: string | null;
  name?: string;
  country?: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  renewable_type?: string | null;
  technology_code?: string | null;
  registration_authority?: string | null;
  relevant_link?: string | null;
  capacity?: number | null;
  capacity_units?: string;
  cod?: string | null;
  status?: EcolabelStatus;
  public_notes?: string | null;
  registry_device_id?: number | null;
  date_reviewed?: string | null;
  valid_from?: string | null;
  valid_through?: string | null;
}

export interface EcolabelDeviceListResponse {
  devices: EcolabelDevice[];
  total: number;
}

export interface EcolabelDeviceFilters {
  standard?: EcolabelStandard | null;
  status?: EcolabelStatus | null;
  country?: string | null;
  renewable_type?: string | null;
  search?: string | null;
}

export interface AiAutofillResponse {
  suggested_device: EcolabelDeviceCreateRequest | null;
  confidence: number;
  extracted_fields: Record<string, string>;
}

export interface AiDeviceMatchResponse {
  matched_devices: EcolabelDevice[];
  confidence_scores: Record<number, number>;
  suggested_mwh: Record<number, number>;
}

export interface FilterOption {
  value: string;
  label: string;
}

// --- Cancellation Statement Types ---

export enum CancellationStatementStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface CancellationStatementDeviceLink {
  device_id: number;
  mwh_attributed: number | null;
}

export interface CancellationStatementDeviceResponse {
  id: number;
  device_id: number;
  device_name: string;
  device_country: string;
  mwh_attributed: number | null;
}

export interface StatementDocument {
  upload_id: number;
  file_url: string;
  file_name: string;
  created_at: string;
}

export interface CancellationStatement {
  id: number;
  uploaded_by_account_id: number;
  company_id: number;
  cancellation_id: number | null;
  company_name: string | null;
  file_url: string;
  file_name: string;
  statement_upload_id: number | null;
  beneficiary_name: string | null;
  registry_code: string | null;
  country: string | null;
  period_start: string | null;
  period_end: string | null;
  purpose: string | null;
  total_mwh: number | null;
  notes: string | null;
  status: CancellationStatementStatus;
  reviewed_by_account_id: number | null;
  reviewed_at: string | null;
  certificate_file_url: string | null;
  certificate_upload_id: number | null;
  created_at: string;
  updated_at: string;
  devices: CancellationStatementDeviceResponse[];
  statement_documents: StatementDocument[];
}

export interface CancellationStatementListResponse {
  statements: CancellationStatement[];
  total: number;
}

export interface UploadStatementResponse {
  statement: CancellationStatement;
  ai_matching_failed?: boolean;
  ai_matches: {
    device: EcolabelDevice;
    confidence: number;
    suggested_mwh: number;
  }[];
  ai_extracted_details: {
    beneficiary_name: string | null;
    registry_code: string | null;
    country: string | null;
    period_start: string | null;
    period_end: string | null;
    purpose: string | null;
    total_mwh: number | null;
    notes: string | null;
  };
}

// --- Device submission + duplicates (DEV-777) ---

export interface DeviceRowValidation {
  missing_required: string[];
  missing_recommended: string[];
  registration_id_quality: RegistrationIdQuality;
}

export interface DeviceDuplicateCandidate {
  /** Exactly one of the two is set: device_id for a catalog match, draft_index for an
   * intra-batch one. The other comes back null. */
  device_id: number | null;
  draft_index: number | null;
  /** Grid uid of the other row for intra-batch candidates (set client-side). */
  draft_uid?: string;
  name: string | null;
  status: string | null;
  score: number;
  reasons: string[];
}

/** One row of the chat's review-grid payload (device fields + flags). */
export interface ExtractedDeviceRow {
  name: string | null;
  country: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  renewable_type: string | null;
  technology_code: string | null;
  plant_ecolabel_id: string | null;
  registration_authority: string | null;
  capacity_value: number | null;
  capacity_unit: string | null;
  cod: string | null;
  relevant_link: string | null;
  public_notes: string | null;
  validation: DeviceRowValidation;
  duplicates: DeviceDuplicateCandidate[];
}

export interface DeviceChatResult {
  devices: ExtractedDeviceRow[];
  upload_ids: number[];
}

export interface SubmitDevicesRequest {
  company_id: number;
  devices: EcolabelDeviceCreateRequest[];
  conversation_id?: number | null;
  upload_ids?: number[];
}

export interface SubmitDevicesResponse {
  batch_id: number;
  created_count: number;
  flagged_duplicate_pairs: number;
  devices: EcolabelDevice[];
}

export interface SubmissionBatchSummary {
  id: number;
  company_id: number;
  company_name: string | null;
  account_id: number;
  created_at: string;
  device_count: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  /** Devices resolved by merging into an existing catalog device (soft-deleted rows —
   * counted so a reviewed batch never looks like it lost devices). */
  merged_count: number;
  flagged_duplicate_count: number;
}

/** One internal EKOenergy review note; admin-only in both directions. */
export interface DeviceNote {
  id: number;
  note: string;
  author_name: string;
  created_at: string;
}

export interface SubmissionBatchListResponse {
  batches: SubmissionBatchSummary[];
  total: number;
}

export type DuplicatePairStatus = "PENDING" | "NOT_DUPLICATE" | "MERGED";

export interface DuplicateAiAssessment {
  recommendation: "duplicate" | "not_duplicate" | "unsure";
  confidence: number;
  rationale: string;
}

export interface DuplicatePair {
  id: number;
  score: number;
  match_reasons: string[];
  status: DuplicatePairStatus;
  ai_assessment: DuplicateAiAssessment | null;
  resolved_by_account_id: number | null;
  resolved_at: string | null;
  device_a: EcolabelDevice;
  device_b: EcolabelDevice;
}

export interface DuplicatePairListResponse {
  pairs: DuplicatePair[];
  total: number;
}

export interface AuthorizedSellerCompany {
  id: number;
  name: string;
}

export interface AuthorizedSellerCompanyListResponse {
  companies: AuthorizedSellerCompany[];
}

export interface CompanyCancellationSummary {
  id: number;
  company_id: number;
  beneficiary_name: string;
  period_start: string;
  period_end: string;
  mwh_cancelled: number;
  registry_country_region: string;
  uploaded_document_count: number;
}

export interface CompanyCancellationListResponse {
  cancellations: CompanyCancellationSummary[];
  total_count: number;
}
